import { Bluetooth, Mic, MicOff, PhoneOff, Settings2, Volume2, VolumeX } from "lucide-react";
import { type AudioRoute, ROUTE_LABEL, nextRoute } from "../../lib/audioRoute";

type Device = { deviceId: string; label?: string | null };

type Props = {
  phase: "idle" | "incoming" | "outgoing" | "in_call" | "ended";
  peerName?: string;
  busy?: boolean;
  error?: string;
  remoteCount?: number;
  needsAudioUnlock?: boolean;
  mediaState?: "idle" | "connecting" | "connected" | "reconnecting" | "disconnected" | "failed";
  muted?: boolean;
  deafened?: boolean;
  devicesOpen?: boolean;
  inputs?: Device[];
  outputs?: Device[];
  selectedInputId?: string | null;
  selectedOutputId?: string | null;
  durationSeconds?: number;
  finalMessage?: string;
  peerReconnecting?: boolean;
  onUnlockAudio: () => void;
  onAnswer: () => void;
  onDecline: () => void;
  onCancel: () => void;
  onEnd: () => void;
  onToggleMute: () => void;
  onToggleDeafen: () => void;
  onToggleDevices: () => void;
  onRefreshDevices: () => void;
  onSelectInput: (id: string | null) => void;
  onSelectOutput: (id: string | null) => void;
  onDismissEnded: () => void;
  audioRoute?: AudioRoute;
  availableRoutes?: AudioRoute[];
  onChangeAudioRoute?: (route: AudioRoute) => void;
};

function formatDuration(seconds = 0) {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`;
}

export default function DirectCallOverlay({
  phase,
  peerName,
  busy,
  error,
  remoteCount,
  needsAudioUnlock,
  mediaState = "idle",
  muted = false,
  deafened = false,
  devicesOpen = false,
  inputs = [],
  outputs = [],
  selectedInputId = null,
  selectedOutputId = null,
  durationSeconds = 0,
  finalMessage = "",
  peerReconnecting = false,
  onUnlockAudio,
  onAnswer,
  onDecline,
  onCancel,
  onEnd,
  onToggleMute,
  onToggleDeafen,
  onToggleDevices,
  onRefreshDevices,
  onSelectInput,
  onSelectOutput,
  onDismissEnded,
  audioRoute = 'earpiece',
  availableRoutes = [],
  onChangeAudioRoute,
}: Props) {
  if (phase === "idle") return null;

  const isConnected = mediaState === "connected";
  const connectionLabel =
    mediaState === "connected" ? "Connected" :
    mediaState === "connecting" ? "Connecting audio" :
    mediaState === "reconnecting" ? "Reconnecting" :
    mediaState === "failed" ? "Audio connection failed" :
    mediaState === "disconnected" ? "Disconnected" :
    "Waiting";
  const outputLabel = outputs.find((device) => device.deviceId === selectedOutputId)?.label || "System output";
  const title = phase === "incoming" ? "Incoming Call" : phase === "outgoing" ? "Calling" : phase === "ended" ? "Call Ended" : "Direct Call";
  const subtitle =
    phase === "incoming" ? `${peerName || "Someone"} is calling` :
    phase === "outgoing" ? `Waiting for ${peerName || "recipient"} to answer` :
    phase === "ended" ? finalMessage || "Call ended." :
    `${Math.max(1, (remoteCount || 0) + 1)} connected`;
  const iconButton = "flex h-12 w-12 items-center justify-center rounded-full bg-white/[0.06] text-fg-2 transition hover:bg-white/[0.11] hover:text-white disabled:cursor-not-allowed disabled:opacity-30";
  const enabledButton = "bg-emerald-400/12 text-emerald-100";
  const offButton = "bg-rose-500/16 text-rose-100";

  return (
    <div className="fixed inset-0 z-[7000] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
      <div className="relative w-full max-w-md overflow-hidden rounded-[30px] bg-[#091216]/95 p-5 shadow-[0_30px_100px_rgba(0,0,0,0.7)] ring-1 ring-emerald-300/10">
        <div className="relative flex flex-col items-center text-center">
          <div className="text-xs text-emerald-300/70">{title}</div>
          <div className="mt-6 flex h-20 w-20 items-center justify-center rounded-full bg-white/[0.06] text-3xl font-medium text-emerald-50 ring-1 ring-emerald-300/25">
            {(peerName || "?").slice(0, 1).toUpperCase()}
          </div>
          <div className="mt-4 text-xl font-medium text-white">{peerName || "Direct Call"}</div>
          <div className="mt-1 text-sm text-fg-3">{subtitle}</div>
          {phase === "incoming" && (
            <div className="mt-4 flex items-center gap-2 rounded-full bg-emerald-400/[0.08] px-4 py-2 text-xs text-emerald-100 ring-1 ring-emerald-300/15">
              <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-300" />
              Ringing on this device
            </div>
          )}

          {phase === "in_call" && (
            <div className="mt-4 flex items-center gap-3 rounded-full bg-white/[0.04] px-4 py-2 text-xs ring-1 ring-white/[0.06]">
              <span className={`h-2 w-2 rounded-full ${isConnected ? "bg-emerald-400" : mediaState === "reconnecting" ? "bg-amber-300" : "bg-emerald-300/60"}`} />
              <span className={isConnected ? "text-emerald-200" : "text-fg-2"}>{connectionLabel}</span>
              <span className="text-fg-3">|</span>
              <span className="tabular-nums text-fg-2">{formatDuration(durationSeconds)}</span>
            </div>
          )}
        </div>

        {needsAudioUnlock && (
          <div className="relative mt-5 flex items-center justify-between gap-3 rounded-2xl bg-amber-400/10 px-4 py-3 text-xs text-amber-100 ring-1 ring-amber-400/25">
            <span>Audio playback is paused.</span>
            <button className="rounded-full bg-amber-300/15 px-3 py-1.5 font-medium hover:bg-amber-300/25" onClick={onUnlockAudio}>
              Enable audio
            </button>
          </div>
        )}

        {phase === "in_call" && peerReconnecting && (
          <div className="relative mt-5 rounded-2xl bg-amber-400/10 px-4 py-3 text-xs text-amber-100 ring-1 ring-amber-400/25">
            The other participant lost connection. Waiting briefly for them to reconnect.
          </div>
        )}

        {error ? (
          <div className="relative mt-5 rounded-2xl bg-rose-500/10 px-4 py-3 text-xs text-rose-100 ring-1 ring-rose-400/20">
            {error}
          </div>
        ) : null}

        {phase === "in_call" && devicesOpen && (
          <div className="relative mt-5 rounded-2xl bg-black/30 p-4 ring-1 ring-white/10">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <div className="text-xs text-emerald-300/70">Devices</div>
                <div className="mt-1 text-xs text-fg-3">Output: {outputLabel}</div>
              </div>
              <button type="button" className="rounded-full px-3 py-1 text-xs text-fg-2 hover:bg-white/5 hover:text-white" onClick={onRefreshDevices}>
                Refresh
              </button>
            </div>
            <div className="space-y-3 text-left">
              <label className="block space-y-1.5">
                <span className="text-meta text-fg-3">Microphone</span>
                <select
                  className="w-full rounded-xl bg-neutral-900 px-3 py-2 text-sm text-white outline-none ring-1 ring-white/10 focus:ring-emerald-300/40" style={{ colorScheme: 'dark' }}
                  value={selectedInputId || ""}
                  onChange={(event) => onSelectInput(event.target.value || null)}
                >
                  <option value="">System default</option>
                  {inputs.map((device) => <option key={device.deviceId} value={device.deviceId}>{device.label || device.deviceId}</option>)}
                </select>
              </label>
              <label className="block space-y-1.5">
                <span className="text-meta text-fg-3">Output</span>
                <select
                  className="w-full rounded-xl bg-neutral-900 px-3 py-2 text-sm text-white outline-none ring-1 ring-white/10 focus:ring-emerald-300/40" style={{ colorScheme: 'dark' }}
                  value={selectedOutputId || ""}
                  onChange={(event) => onSelectOutput(event.target.value || null)}
                >
                  <option value="">System default</option>
                  {outputs.map((device) => <option key={device.deviceId} value={device.deviceId}>{device.label || device.deviceId}</option>)}
                </select>
              </label>
            </div>
          </div>
        )}

        <div className="relative mt-6 flex items-center justify-center gap-3">
          {phase === "incoming" && (
            <>
              <button className="h-12 rounded-full bg-rose-500/15 px-6 text-sm font-medium text-rose-100 hover:bg-rose-500/25 disabled:opacity-60" onClick={onDecline} disabled={!!busy}>Decline</button>
              <button className="h-12 rounded-full bg-emerald-400 px-7 text-sm font-semibold text-neutral-950 hover:bg-emerald-300 disabled:opacity-60" onClick={onAnswer} disabled={!!busy}>Answer</button>
            </>
          )}
          {phase === "outgoing" && (
            <button className="h-12 rounded-full bg-rose-500/15 px-8 text-sm font-medium text-rose-100 hover:bg-rose-500/25 disabled:opacity-60" onClick={onCancel} disabled={!!busy}>Cancel call</button>
          )}
          {phase === "ended" && (
            <button className="h-12 rounded-full bg-white/[0.07] px-8 text-sm font-medium text-fg-2 hover:bg-white/[0.12]" type="button" onClick={onDismissEnded}>
              Close
            </button>
          )}
          {phase === "in_call" && (
            <>
              {/* Top row — call controls */}
              <button className={`${iconButton} ${muted ? offButton : enabledButton}`} type="button" onClick={onToggleMute} disabled={!!busy || mediaState === "failed"} aria-label={muted ? "Unmute microphone" : "Mute microphone"} title={muted ? "Unmute microphone" : "Mute microphone"}>
                {muted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
              </button>
              <button className={`${iconButton} ${deafened ? offButton : enabledButton}`} type="button" onClick={onToggleDeafen} disabled={!!busy} aria-label={deafened ? "Restore call audio" : "Deafen call audio"} title={deafened ? "Restore call audio" : "Deafen call audio"}>
                {deafened ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
              </button>
              {availableRoutes.length > 1 && onChangeAudioRoute && (
                <button
                  className={`${iconButton} ${audioRoute === 'bluetooth' ? enabledButton : ''}`}
                  type="button"
                  onClick={() => onChangeAudioRoute(nextRoute(audioRoute, availableRoutes))}
                  title={`Audio: ${ROUTE_LABEL[audioRoute]} — tap to switch`}
                  aria-label={`Audio output: ${ROUTE_LABEL[audioRoute]}`}
                >
                  {audioRoute === 'bluetooth' ? (
                    <Bluetooth className="h-5 w-5" />
                  ) : audioRoute === 'speaker' ? (
                    <Volume2 className="h-5 w-5" />
                  ) : (
                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
                  )}
                  <span className="sr-only">{ROUTE_LABEL[audioRoute]}</span>
                </button>
              )}
              <button className={`${iconButton} ${devicesOpen ? enabledButton : ""}`} type="button" onClick={onToggleDevices} disabled={!!busy} aria-label="Audio device settings" title="Audio device settings">
                <Settings2 className="h-5 w-5" />
              </button>
            </>
          )}
        </div>

        {/* Disconnect — own row, larger, clearly red so it can't be missed */}
        {phase === "in_call" && (
          <div className="mt-4 flex justify-center">
            <button
              type="button"
              disabled={!!busy}
              onClick={onEnd}
              aria-label="End call"
              title="End call"
              className="flex items-center gap-2 h-14 rounded-full bg-rose-600 hover:bg-rose-500 active:bg-rose-700 text-white px-10 text-sm font-semibold transition disabled:opacity-50"
            >
              <PhoneOff className="h-5 w-5" />
              End Call
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
